import type { FunctionComponent } from 'react';
import { useCallback, useMemo } from 'react';
import { useAuth } from 'react-oidc-context';
import { useSelector } from 'react-redux';

import { config } from '../../../config/config';
import { Loader } from '../../shared/components/loader/loader';
import { useBalanceChange } from '../../shared/hooks/use-balance-change';
import { useDebounce } from '../../shared/hooks/use-debounce';
import { selectLocale } from '../../shared/store/locale.store';
import { exists } from '../../shared/utils/filters';
import { FreeBetsOverview } from '../components/free-bets/free-bets';
import { IUserProfileFreeBetStatus, useUserAvailableFreeBetsQuery } from '../graphql/query.graphql';

export const FreeBets: FunctionComponent<unknown> = () => {
    const auth = useAuth();
    const locale = useSelector(selectLocale);
    const userProfileId = auth.user?.profile.user_profile_id as string;

    const [{ fetching, data }, reload] = useUserAvailableFreeBetsQuery({
        pause: !userProfileId,
        variables: {
            xTenantId: config.TENANT_ID,
            skinId: config.SKIN_ID,
            userProfileId,
            locale,
        },
        requestPolicy: 'cache-and-network',
    });
    const debounceReload = useDebounce(useCallback(reload, [reload]));

    useBalanceChange(
        useCallback(() => {
            debounceReload();
        }, [debounceReload]),
    );

    const availableFreeBets = useMemo(() => {
        const availableStatuses = [
            IUserProfileFreeBetStatus.Assigned,
            IUserProfileFreeBetStatus.InProgress,
        ];
        return (
            data?.freeBets?.data
                ?.filter((freeBet) => {
                    return (
                        freeBet &&
                        availableStatuses.includes(freeBet.status as IUserProfileFreeBetStatus)
                    );
                })
                .filter(exists) ?? []
        );
    }, [data]);

    const inactiveFreeBets = useMemo(() => {
        const inactiveStatues = [
            IUserProfileFreeBetStatus.Cancelled,
            IUserProfileFreeBetStatus.Consumed,
            IUserProfileFreeBetStatus.Converted,
            IUserProfileFreeBetStatus.Expired,
            IUserProfileFreeBetStatus.Fulfilled,
        ];
        return (
            data?.freeBets?.data
                ?.filter((freeBet) => {
                    return (
                        freeBet &&
                        inactiveStatues.includes(freeBet.status as IUserProfileFreeBetStatus)
                    );
                })
                .filter(exists) ?? []
        );
    }, [data]);

    if (fetching) {
        return <Loader />;
    }

    return (
        <FreeBetsOverview
            availableFreeBets={availableFreeBets}
            inactiveFreeBets={inactiveFreeBets}
        />
    );
};
